/**
 * Capture domain logic on the device (ADR-0006):
 *  - drafts live in offline storage first; every change is saved locally at once
 *  - a debounced `survey.upsertDraft` snapshot is queued after changes (800 ms)
 *  - photos are stored as attachments and their `attachment.upload` is queued
 *    BEFORE the draft snapshot that references them (the outbox keeps order and
 *    moves coalesced snapshots to the back)
 *  - submit/resubmit validate with the form engine and queue the op
 *  - derived answers (customer/site/branch lookups, calculated fields) are
 *    filled on the device too; the server recomputes them (ADR-0028)
 *  - a refused submit returns the survey to editing; a refused file is
 *    removed from the answers and reported, without blocking the survey
 *  - discarding a synced draft tells the server (survey.discard)
 *  - submitted surveys leave the device once fully synced (housekeeping)
 */
import { draftReference } from '@sss/core';
import {
  Answers,
  attachmentIdsOf,
  checkpointIssues,
  deriveAnswers,
  validateSubmission,
  type ControlAnswer,
  type LookupValues,
  type SurveyContext,
  type Template,
  type ValidationIssue,
} from '@sss/form-engine';
import { purgeableSurveys, type OutboxItem } from '@sss/sync';
import type { PackSite, ServerPackSurvey, SurveyRow, SyncOpResult } from '../../lib/api/types';
import { WITHDRAWN_REASONS, type AttachmentKind, type AttachmentRecord, type DraftRecord, type OfflineStorage, type WithdrawnReason } from '../../lib/offline/types';
import type { SyncService } from '../../lib/sync/sync-service';

export interface DraftServiceOptions {
  debounceMs?: number;
  now?: () => number;
  newId?: () => string;
}

export type DraftListener = (draftId: string) => void;

/**
 * Ids of drafts discarded here whose `survey.discard` the server may not have
 * applied when an offline pack was put together: such a pack still lists them
 * as drafts and must not bring them back (C2-3). An id is forgotten once a
 * pack no longer lists it as a draft (the pack carries only DRAFT/RETURNED
 * surveys, so a cancelled one drops out).
 */
export const DISCARDED_DRAFTS_META = 'drafts.discarded';

/** The draft is no longer on the device (discarded meanwhile): nothing to save, nothing to report. */
export const DRAFT_NOT_FOUND = 'DRAFT_NOT_FOUND';

export function emptyAnswers(): Answers {
  return { fields: {}, controls: {} };
}

/** Codes the customer/site/branch lookup fields store for a survey at this site (as the server does). */
export function lookupValuesFor(site: Pick<PackSite, 'code' | 'customerCode' | 'branchCode'> | undefined): LookupValues {
  if (!site) return {};
  return { customer: site.customerCode ?? undefined, site: site.code, branch: site.branchCode ?? null };
}

export function surveyContextFor(site: Pick<PackSite, 'regionCode' | 'countryCode' | 'serviceLine' | 'branchCode'> | undefined): SurveyContext {
  return {
    site: {
      region: site?.regionCode ?? null,
      country: site?.countryCode ?? null,
      serviceLine: site?.serviceLine ?? null,
      branch: site?.branchCode ?? null,
    },
  };
}

export class DraftService {
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly listeners = new Set<DraftListener>();
  private readonly debounceMs: number;
  private readonly now: () => number;
  private readonly newId: () => string;

  constructor(
    private readonly storage: OfflineStorage,
    private readonly sync: SyncService,
    opts: DraftServiceOptions = {},
  ) {
    this.debounceMs = opts.debounceMs ?? 800;
    this.now = opts.now ?? (() => Date.now());
    this.newId = opts.newId ?? (() => crypto.randomUUID());
  }

  subscribe(l: DraftListener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  private changed(id: string) {
    this.listeners.forEach((l) => l(id));
  }

  get(id: string): Promise<DraftRecord | undefined> {
    return this.storage.getDraft(id);
  }

  list(): Promise<DraftRecord[]> {
    return this.storage.listDrafts();
  }

  /** A new draft; with its template, lookups and calculated fields are filled at once (C1). */
  async create(input: { site: PackSite; surveyType: string; templateVersionId: string; reporterUserId: string; template?: Template }): Promise<DraftRecord> {
    const id = this.newId();
    const t = this.now();
    const answers = input.template ? deriveAnswers(input.template, emptyAnswers(), lookupValuesFor(input.site)) : emptyAnswers();
    const draft: DraftRecord = {
      id,
      siteId: input.site.id,
      customerId: input.site.customerId,
      surveyType: input.surveyType,
      templateVersionId: input.templateVersionId,
      status: 'DRAFT',
      humanId: null,
      localRef: draftReference(id),
      surveyDate: null,
      answers,
      flags: {},
      reporterUserId: input.reporterUserId,
      createdAt: t,
      updatedAt: t,
      syncState: 'local',
    };
    await this.storage.putDraft(draft);
    this.changed(id);
    return draft;
  }

  /**
   * Bring a server survey onto the device (e.g. one returned by the analyst,
   * or a draft from another device after taking over its lock).
   */
  async importSurveyRow(
    s: Pick<SurveyRow, 'id' | 'human_id' | 'survey_type' | 'template_version_id' | 'site_id' | 'customer_id' | 'status' | 'answers' | 'flags' | 'survey_date' | 'reporter_user_id'>,
  ): Promise<DraftRecord> {
    const existing = await this.storage.getDraft(s.id);
    const draft: DraftRecord = {
      id: s.id,
      siteId: s.site_id,
      customerId: s.customer_id,
      surveyType: s.survey_type,
      templateVersionId: s.template_version_id,
      status: s.status,
      humanId: s.human_id,
      localRef: existing?.localRef ?? draftReference(s.id),
      surveyDate: s.survey_date,
      answers: Answers.parse(s.answers ?? emptyAnswers()),
      flags: s.flags ?? {},
      reporterUserId: s.reporter_user_id,
      createdAt: existing?.createdAt ?? this.now(),
      updatedAt: this.now(),
      syncState: 'synced',
    };
    await this.storage.putDraft(draft);
    this.changed(s.id);
    return draft;
  }

  /**
   * Apply the reporter's DRAFT/RETURNED surveys from /sync/pack:
   *  - a survey the analyst returned becomes editable on the device (flagged items only);
   *  - a draft missing from this device (new phone, reinstall) is restored, unless
   *    another device holds its lock (open it from /surveys/:id to take over);
   *  - local work waiting in the outbox always wins — nothing unsynced is overwritten.
   */
  async reconcileServerSurveys(
    surveys: ServerPackSurvey[],
    ctx: { reporterUserId: string; deviceId: string; customerOf: (siteId: string) => string | undefined },
  ): Promise<number> {
    let changed = 0;
    const outbox = await this.storage.all();
    for (const s of surveys) {
      const pending = outbox.some((i) => i.entityId === s.id);
      const local = await this.storage.getDraft(s.id);
      // Given back to this reporter after it was reassigned away: the server's
      // copy wins (someone else may have worked on it meanwhile; the edits
      // refused here were already reported as unsendable), and it is editable
      // again — the first save claims the free lock.
      if (local?.withdrawn?.reason === 'SURVEY_REASSIGNED') {
        const customerId = local.customerId;
        await this.sync.discard(s.id);
        await this.serial(s.id, async () => {
          await this.importSurveyRow({ ...s, customer_id: customerId, reporter_user_id: ctx.reporterUserId });
        });
        changed++;
        continue;
      }
      if (pending) continue;
      const customerId = local?.customerId ?? ctx.customerOf(s.site_id);
      if (!customerId) continue;
      const row = { ...s, customer_id: customerId, reporter_user_id: ctx.reporterUserId };
      if (!local) {
        const lockedElsewhere = s.status === 'DRAFT' && !!s.lock_device_id && s.lock_device_id !== ctx.deviceId;
        if (lockedElsewhere) continue;
        // In line with a discard of the same draft: either it runs first and leaves its
        // tombstone, or it runs after and removes what was imported.
        const imported = await this.serial(s.id, async () => {
          if (await this.storage.getDraft(s.id)) return false;
          if (s.status === 'DRAFT' && (await this.discardedIds()).includes(s.id)) return false;
          await this.importSurveyRow(row);
          return true;
        });
        if (imported) changed++;
      } else if (s.status === 'RETURNED' && local.status !== 'RETURNED') {
        await this.serial(s.id, async () => {
          await this.importSurveyRow(row);
        });
        changed++;
      } else if (!local.humanId && s.human_id) {
        await this.storage.putDraft({ ...local, humanId: s.human_id });
        this.changed(s.id);
        changed++;
      }
    }
    // A discarded draft the server no longer lists as a draft is cancelled there (or went on
    // elsewhere and was submitted): forget it, so it can come back if it is returned.
    const drafts = new Set(surveys.filter((s) => s.status === 'DRAFT').map((s) => s.id));
    await this.updateDiscarded((ids) => ids.filter((id) => drafts.has(id)));
    return changed;
  }

  private async discardedIds(): Promise<string[]> {
    return (await this.storage.getMeta<string[]>(DISCARDED_DRAFTS_META)) ?? [];
  }

  /** Read-modify-write of the discarded ids, one at a time. */
  private updateDiscarded(fn: (ids: string[]) => string[]): Promise<void> {
    return this.serial(DISCARDED_DRAFTS_META, async () => {
      const cur = await this.discardedIds();
      const next = fn(cur);
      if (next.length !== cur.length || next.some((id, i) => id !== cur[i])) await this.storage.setMeta(DISCARDED_DRAFTS_META, next);
    });
  }

  private readonly chains = new Map<string, Promise<unknown>>();

  /** Run writes to one draft one after another, so fast typing never loses a change. */
  private serial<T>(id: string, fn: () => Promise<T>): Promise<T> {
    const prev = this.chains.get(id) ?? Promise.resolve();
    const next = prev.then(fn, fn);
    this.chains.set(
      id,
      next.catch(() => {}),
    );
    return next;
  }

  /** Apply a change to the answers; saved locally now, queued for sync after a pause. */
  update(id: string, mutate: (a: Answers) => Answers): Promise<DraftRecord> {
    return this.serial(id, async () => {
      const d = await this.storage.getDraft(id);
      if (!d) throw new Error(DRAFT_NOT_FOUND);
      if (!isEditable(d)) throw new Error('DRAFT_NOT_EDITABLE');
      const answers = mutate(structuredClone(d.answers));
      const surveyDate = typeof answers.fields.surveyDate === 'string' ? answers.fields.surveyDate : d.surveyDate;
      const next: DraftRecord = { ...d, answers, surveyDate, updatedAt: this.now(), syncState: d.syncState === 'rejected' ? 'rejected' : 'pending' };
      await this.storage.putDraft(next);
      this.changed(id);
      this.scheduleSave(id);
      return next;
    });
  }

  /** Wait for queued local writes of a draft to finish. */
  settled(id: string): Promise<void> {
    return (this.chains.get(id) ?? Promise.resolve()).then(() => {});
  }

  setField(id: string, key: string, value: unknown) {
    return this.update(id, (a) => applyField(a, key, value));
  }

  setControl(id: string, code: string, patch: Partial<ControlAnswer>) {
    return this.update(id, (a) => applyControl(a, code, patch));
  }

  private scheduleSave(id: string) {
    const t = this.timers.get(id);
    if (t) clearTimeout(t);
    if (this.debounceMs <= 0) {
      void this.serial(id, () => this.saveNow(id));
      return;
    }
    this.timers.set(
      id,
      // The timer stays registered until saveNow has queued the snapshot, so the
      // draft never reads "synced" in between (onResult).
      setTimeout(() => void this.serial(id, () => this.saveNow(id)), this.debounceMs),
    );
  }

  /** Queue the current snapshot immediately (also used before submit). */
  async saveNow(id: string): Promise<OutboxItem | null> {
    const pending = this.timers.get(id);
    if (pending) {
      clearTimeout(pending);
      this.timers.delete(id);
    }
    const d = await this.storage.getDraft(id);
    if (!d) return null;
    return this.queueSnapshot(d);
  }

  private queueSnapshot(d: DraftRecord): Promise<OutboxItem> {
    return this.sync.enqueue('survey.upsertDraft', d.id, {
      survey: {
        id: d.id,
        siteId: d.siteId,
        surveyType: d.surveyType,
        templateVersionId: d.templateVersionId,
        surveyDate: d.surveyDate,
        answers: d.answers,
        clientUpdatedAt: new Date(d.updatedAt).toISOString(),
      },
    });
  }

  /**
   * Store a file and queue its upload before the snapshot that references it.
   * In line with the draft's other writes: a file for a draft discarded
   * meanwhile is neither stored nor uploaded (throws DRAFT_NOT_FOUND).
   */
  async addAttachment(
    surveyId: string,
    ref: string,
    kind: AttachmentKind,
    blob: Blob,
    meta: { fileName?: string; lat?: number | null; lng?: number | null; takenAt?: string | null } = {},
  ): Promise<AttachmentRecord> {
    const id = this.newId();
    const ext = blob.type === 'image/jpeg' ? 'jpg' : blob.type === 'image/png' ? 'png' : blob.type.split('/')[1] ?? 'bin';
    const a: AttachmentRecord = {
      id,
      surveyId,
      kind,
      ref,
      data: await blobToArrayBuffer(blob),
      contentType: blob.type || 'application/octet-stream',
      fileName: meta.fileName ?? `${kind.toLowerCase()}-${id.slice(0, 8)}.${ext}`,
      size: blob.size,
      lat: meta.lat ?? null,
      lng: meta.lng ?? null,
      takenAt: meta.takenAt ?? new Date(this.now()).toISOString(),
      uploaded: false,
      createdAt: this.now(),
    };
    return this.serial(surveyId, async () => {
      if (!(await this.storage.getDraft(surveyId))) throw new Error(DRAFT_NOT_FOUND);
      await this.storage.putAttachment(a);
      await this.sync.enqueue('attachment.upload', surveyId, { attachmentId: id }, { flush: false });
      return a;
    });
  }

  async removeAttachment(attachmentId: string): Promise<void> {
    await this.storage.deleteAttachment(attachmentId);
  }

  async attachmentUrl(id: string): Promise<string | null> {
    const a = await this.storage.getAttachment(id);
    return a ? URL.createObjectURL(new Blob([a.data], { type: a.contentType })) : null;
  }

  /**
   * A QR/NFC tag scanned at a question (ADR-0034). Shown as PENDING at once and
   * queued as `survey.checkpointScan`; the server verifies the signed tag and
   * site, and the result marks it VERIFIED (with any GPS flags) or REJECTED.
   * A later scan of the same question replaces the earlier one.
   */
  recordCheckpointScan(
    id: string,
    code: string,
    scan: { payload: string; method: 'QR' | 'NFC'; lat?: number | null; lng?: number | null; accuracy?: number | null },
  ): Promise<NonNullable<ControlAnswer['checkpointScan']>> {
    const entry: NonNullable<ControlAnswer['checkpointScan']> = { scanId: this.newId(), method: scan.method, scannedAt: new Date(this.now()).toISOString(), status: 'PENDING' };
    return this.serial(id, async () => {
      const d = await this.storage.getDraft(id);
      if (!d) throw new Error(DRAFT_NOT_FOUND);
      if (!isEditable(d)) throw new Error('DRAFT_NOT_EDITABLE');
      const next: DraftRecord = { ...d, answers: applyControl(d.answers, code, { checkpointScan: entry }), updatedAt: this.now(), syncState: 'pending' };
      await this.storage.putDraft(next);
      this.changed(id);
      await this.sync.enqueue('survey.checkpointScan', id, {
        surveyId: id,
        scanId: entry.scanId,
        payload: scan.payload,
        method: scan.method,
        scannedAt: entry.scannedAt,
        ...(scan.lat != null && scan.lng != null ? { lat: scan.lat, lng: scan.lng } : {}),
        ...(scan.accuracy != null ? { accuracy: scan.accuracy } : {}),
      });
      this.scheduleSave(id);
      return entry;
    });
  }

  /** Validate and queue submission. Returns validation issues (empty = submitted). */
  async submit(id: string, template: Template, context: SurveyContext): Promise<ValidationIssue[]> {
    await this.settled(id);
    const d = await this.storage.getDraft(id);
    if (!d) throw new Error(`Draft ${id} not found`);
    const issues = submissionIssues(template, d.answers, context);
    if (issues.length > 0) return issues;
    const op = d.status === 'RETURNED' ? 'survey.resubmit' : 'survey.submit';
    await this.saveNow(id);
    const { rejectedReason: _r, rejectedDetails: _d, ...rest } = d;
    await this.storage.putDraft({ ...rest, status: 'SUBMITTED', updatedAt: this.now(), syncState: 'pending' });
    this.changed(id);
    await this.sync.enqueue(op, id, { surveyId: id });
    return [];
  }

  /**
   * Remove a survey from the device. A draft the server may know about is
   * discarded there too (it becomes CANCELLED), so it does not come back with
   * the next offline pack or on Home (A1). A survey the server withdrew
   * (cancelled, reassigned) is only removed here. Whether this copy is still
   * the live one is the server's call: while another device holds the lock,
   * the discard changes nothing there (a LOCK_LOST seen here may be stale
   * after "Take over", C2-4).
   */
  discard(id: string): Promise<void> {
    // In line with the sync results for this survey, so none of them writes the draft back afterwards.
    return this.serial(id, async () => {
      const t = this.timers.get(id);
      if (t) clearTimeout(t);
      this.timers.delete(id);
      const d = await this.storage.getDraft(id);
      const serverMayKnow = !!d && (!!d.humanId || d.syncState !== 'local');
      const tellServer = !!d && serverMayKnow && d.status === 'DRAFT' && !d.withdrawn;
      // Before the draft goes, so a pack being reconciled meanwhile cannot restore it.
      if (tellServer) await this.updateDiscarded((ids) => (ids.includes(id) ? ids : [...ids, id]));
      await this.sync.discard(id);
      for (const a of await this.storage.listAttachments(id)) await this.storage.deleteAttachment(a.id);
      await this.storage.deleteDraft(id);
      this.changed(id);
      if (tellServer) await this.sync.enqueue('survey.discard', id, { surveyId: id });
    });
  }

  /**
   * Housekeeping (C22, ADR-0028): surveys past RETURNED whose answers and
   * files are all on the server leave the device after a grace period.
   */
  async purgeSynced(now = this.now()): Promise<string[]> {
    const outbox = await this.storage.all();
    const states = [];
    for (const d of await this.storage.listDrafts()) {
      const files = await this.storage.listAttachments(d.id);
      states.push({
        id: d.id,
        status: d.status,
        syncState: d.syncState,
        updatedAt: d.updatedAt,
        withdrawn: !!d.withdrawn,
        queued: outbox.filter((i) => i.entityId === d.id).length,
        unsentAttachments: files.filter((f) => !f.uploaded).length,
      });
    }
    const ids = purgeableSurveys(states, now);
    for (const id of ids) {
      for (const a of await this.storage.listAttachments(id)) await this.storage.deleteAttachment(a.id);
      await this.storage.deleteDraft(id);
      this.changed(id);
    }
    return ids;
  }

  /** The reporter has read the "file refused" notice. */
  async dismissRefusedFiles(id: string): Promise<void> {
    await this.serial(id, async () => {
      const d = await this.storage.getDraft(id);
      if (!d?.refusedFiles) return;
      const { refusedFiles: _, ...rest } = d;
      await this.storage.putDraft(rest);
      this.changed(id);
    });
  }

  // ---- hooks from the sync transport --------------------------------------------------
  onResult(item: OutboxItem, data: SyncOpResult['data'] | undefined): Promise<void> {
    if (item.op === 'attachment.upload' || item.op === 'survey.discard') return Promise.resolve();
    return this.serial(item.entityId, async () => {
      const d = await this.storage.getDraft(item.entityId);
      if (!d) return;
      const next: DraftRecord = { ...d };
      if (data?.humanId) next.humanId = data.humanId;
      // Only a submit/resubmit moves the status on. A snapshot's ack reports the
      // server status *before* a submit queued behind it, so it must never lower
      // a local SUBMITTED back to DRAFT (C8), nor touch a local edit.
      if (data?.status && (item.op === 'survey.submit' || item.op === 'survey.resubmit')) next.status = data.status;
      if (item.op === 'survey.checkpointScan') next.answers = withScanOutcome(d.answers, (item.payload as { scanId: string }).scanId, { status: 'VERIFIED', flags: Array.isArray(data?.flags) ? (data.flags as string[]) : [] });
      const remaining = (await this.storage.all()).filter((i) => i.entityId === d.id && i.id !== item.id).length;
      // A change still waiting for its autosave is not on the server yet.
      next.syncState = remaining === 0 && !this.timers.has(d.id) ? 'synced' : 'pending';
      delete next.rejectedReason;
      delete next.rejectedDetails;
      await this.storage.putDraft(next);
      this.changed(d.id);
    });
  }

  /**
   * The server refused an operation. Cancelled/reassigned surveys are
   * withdrawn. If a submit was on its way, the survey goes back to editing
   * (DRAFT, or RETURNED for a resubmit) and the queued submit is withdrawn,
   * so the reporter can fix the problem and submit again (C8); a refused
   * submit itself is then dropped instead of blocking the survey.
   */
  onReject(item: OutboxItem, error: string, details?: unknown): Promise<'drop' | void> {
    return this.serial(item.entityId, async () => {
      const d = await this.storage.getDraft(item.entityId);
      // Removed from the device meanwhile: nothing here waits for this item any more.
      if (!d) return 'drop';
      // A tag the server refused (not genuine, replaced, another site): marked on the question, and the survey carries on.
      if (item.op === 'survey.checkpointScan' && !WITHDRAWN_REASONS.includes(error)) {
        await this.storage.putDraft({ ...d, answers: withScanOutcome(d.answers, (item.payload as { scanId: string }).scanId, { status: 'REJECTED', error }) });
        this.changed(d.id);
        return 'drop';
      }
      if (WITHDRAWN_REASONS.includes(error)) {
        await this.storage.putDraft({ ...d, ...withdrawnState(d, error as WithdrawnReason, d.withdrawn?.note), syncState: 'rejected', rejectedReason: error, rejectedDetails: details });
        this.changed(d.id);
        return;
      }
      const reverted = await this.withdrawQueuedSubmit(d, item);
      await this.storage.putDraft({ ...d, ...reverted, syncState: 'rejected', rejectedReason: error, rejectedDetails: details });
      this.changed(d.id);
      return isSubmitOp(item.op) ? 'drop' : undefined;
    });
  }

  /** Remove queued submits of this survey (other than `current`) and say which status to return to. */
  private async withdrawQueuedSubmit(d: DraftRecord, current: OutboxItem | null): Promise<Partial<DraftRecord>> {
    const queued = (await this.storage.all()).filter((i) => i.entityId === d.id && isSubmitOp(i.op) && i.id !== current?.id);
    for (const i of queued) await this.storage.remove(i.id);
    if (d.status !== 'SUBMITTED') return {};
    const submit = current && isSubmitOp(current.op) ? current : queued[0];
    const back = submit ? (submit.op === 'survey.resubmit' ? 'RETURNED' : 'DRAFT') : Object.keys(d.flags).length > 0 ? 'RETURNED' : 'DRAFT';
    return { status: back, updatedAt: this.now() };
  }

  /**
   * The server will never accept this file (type or size, C5): take it out of
   * the answers, delete it, tell the reporter, and queue a fresh snapshot. A
   * submit waiting behind it is withdrawn — the evidence it relied on is gone,
   * so the reporter checks and submits again.
   */
  onAttachmentRefused(item: OutboxItem, attachmentId: string, code: string): Promise<void> {
    return this.serial(item.entityId, async () => {
      const a = await this.storage.getAttachment(attachmentId);
      await this.storage.deleteAttachment(attachmentId);
      const d = await this.storage.getDraft(item.entityId);
      if (!d) return;
      const answers = withoutAttachment(d.answers, attachmentId);
      const reverted = await this.withdrawQueuedSubmit(d, null);
      const refusedFiles = [...(d.refusedFiles ?? []), { fileName: a?.fileName ?? attachmentId, ref: a?.ref ?? '', code }];
      const next: DraftRecord = { ...d, ...reverted, answers, refusedFiles, updatedAt: this.now(), syncState: 'pending' };
      await this.storage.putDraft(next);
      this.changed(d.id);
      if (isEditable(next)) await this.queueSnapshot(next);
    });
  }

  /** Ids of drafts the server already knows, still editable here — sent with the pack request. */
  async syncedEditableIds(): Promise<string[]> {
    return (await this.storage.listDrafts()).filter((d) => d.humanId && isEditable(d)).map((d) => d.id);
  }

  /** Surveys the server cancelled or gave to someone else: stop editing, say why, offer removal. */
  async markWithdrawn(items: { id: string; reason: WithdrawnReason; note: string | null }[]): Promise<void> {
    for (const w of items) {
      await this.serial(w.id, async () => {
        const d = await this.storage.getDraft(w.id);
        if (!d) return;
        await this.storage.putDraft({ ...d, ...withdrawnState(d, w.reason, w.note) });
        this.changed(d.id);
      });
    }
  }
}

function withdrawnState(d: DraftRecord, reason: WithdrawnReason, note: string | null | undefined): Pick<DraftRecord, 'withdrawn' | 'status'> {
  return { withdrawn: { reason, note: note ?? null }, status: reason === 'SURVEY_CANCELLED' ? 'CANCELLED' : d.status };
}

/** Pure answer mutations, shared by the optimistic UI state and the persisted draft. */
export function applyField(a: Answers, key: string, value: unknown): Answers {
  const fields = { ...a.fields };
  if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) delete fields[key];
  else fields[key] = value;
  return { ...a, fields };
}

export function applyControl(a: Answers, code: string, patch: Partial<ControlAnswer>): Answers {
  const cur = a.controls[code] ?? { value: null, photos: [] };
  return { ...a, controls: { ...a.controls, [code]: { ...cur, ...patch, photos: patch.photos ?? cur.photos ?? [] } } };
}

/**
 * What stops a submit, on the device: the form engine's checks, plus a scan
 * for each question whose tag is required (ADR-0034). A scan still on its way
 * counts here; the server has the last word when the submit arrives.
 */
export function submissionIssues(template: Template, answers: Answers, context: SurveyContext): ValidationIssue[] {
  const scanned = new Set(Object.entries(answers.controls).filter(([, c]) => c.checkpointScan && c.checkpointScan.status !== 'REJECTED').map(([code]) => code));
  return [...validateSubmission(template, answers, context), ...checkpointIssues(template, answers, context, scanned)];
}

/** The answers with a scan's server outcome recorded, if that scan is still the question's latest. */
export function withScanOutcome(a: Answers, scanId: string, outcome: { status: 'VERIFIED' | 'REJECTED'; flags?: string[]; error?: string }): Answers {
  const hit = Object.entries(a.controls).find(([, c]) => c.checkpointScan?.scanId === scanId);
  if (!hit) return a;
  const [code, c] = hit;
  const { error: _e, flags: _f, ...scan } = c.checkpointScan!;
  return applyControl(a, code, { checkpointScan: { ...scan, ...outcome } });
}

/** Where a stored file belongs in the answers: a control's evidence photos, or a photo/file/signature field. */
export type FileTarget = { control: string } | { field: string };

/** Answers with new attachment ids added at `target` (a signature field holds one id). */
export function withAttachmentsAdded(a: Answers, target: FileTarget, kind: AttachmentKind, ids: string[]): Answers {
  if (ids.length === 0) return a;
  if ('control' in target) return applyControl(a, target.control, { photos: [...(a.controls[target.control]?.photos ?? []), ...ids] });
  if (kind === 'SIGNATURE') return applyField(a, target.field, ids[ids.length - 1]);
  const cur = a.fields[target.field];
  return applyField(a, target.field, [...(Array.isArray(cur) ? cur : []), ...ids]);
}

/** Only drafts and returned surveys can be edited on the device (workflow isEditableByReporter). */
export function isEditable(d: Pick<DraftRecord, 'status' | 'withdrawn'>): boolean {
  return !d.withdrawn && (d.status === 'DRAFT' || d.status === 'RETURNED');
}

/**
 * For a returned survey, only items the analyst flagged — and the items their
 * answers reveal (`returnedEditableRefs` from @sss/form-engine, passed as
 * `editable`) — can be changed (C11).
 */
export function canEditItem(d: Pick<DraftRecord, 'status' | 'flags' | 'withdrawn'>, ref: string, editable?: ReadonlySet<string> | null): boolean {
  if (d.withdrawn) return false;
  if (d.status === 'DRAFT') return true;
  if (d.status === 'RETURNED') return Object.prototype.hasOwnProperty.call(d.flags, ref) || !!editable?.has(ref);
  return false;
}

const isSubmitOp = (op: string) => op === 'survey.submit' || op === 'survey.resubmit';

/** Answers without one attachment id (photo/file arrays, a signature, control evidence). */
export function withoutAttachment(a: Answers, id: string): Answers {
  const fields: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(a.fields)) {
    if (v === id) continue;
    if (Array.isArray(v) && v.includes(id)) {
      const rest = v.filter((x) => x !== id);
      if (rest.length) fields[k] = rest;
      continue;
    }
    fields[k] = v;
  }
  const controls = Object.fromEntries(Object.entries(a.controls).map(([code, c]) => [code, c.photos?.includes(id) ? { ...c, photos: c.photos.filter((p) => p !== id) } : c]));
  return { ...a, fields, controls };
}

/** Attachment ids a template's fields hold (for the attachments list). */
export function fieldAttachmentIds(template: Template, a: Answers): string[] {
  return template.sections.flatMap((s) => s.items.flatMap((i) => (i.kind === 'field' ? attachmentIdsOf(i, a.fields[i.key]) : [])));
}

export function blobToArrayBuffer(b: Blob): Promise<ArrayBuffer> {
  if (typeof b.arrayBuffer === 'function') return b.arrayBuffer();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as ArrayBuffer);
    r.onerror = () => reject(r.error);
    r.readAsArrayBuffer(b);
  });
}
